import { Component, Input, ViewChild } from '@angular/core';
import { CarePlanFileAddComponent } from './care-plan-file-add/care-plan-file-add.component';
import { ICarePlanFileDetailed } from 'src/app/interfaces/CarePlanFile/ICarePlanFileDetailed';
import { NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { Subject, takeUntil } from 'rxjs';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { Router } from '@angular/router';
import { SwalToastService } from 'src/app/services/swal.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PermissionService } from 'src/app/services/permission.service';
import Swal from 'sweetalert2';
import { ALLOWED_MIME } from 'src/app/guards/file.guard';
import moment from 'moment';
import { date_format, datetime_format } from 'src/date/date.configuration';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzTableModule } from 'ng-zorro-antd/table';
import { CommonModule } from '@angular/common';
import { FlexModule } from '@angular/flex-layout';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { BrowserModule } from '@angular/platform-browser';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { PermissionModule } from 'src/app/modules/permission.module';
import { TooltipComponent } from 'src/app/common/tooltip/tooltip.component';
import { CarePlanFileService } from 'src/app/services/carePlanFile.service';

@Component({
  selector: 'app-care-plan-file',
  imports: [
    NzLayoutModule,
    NzTableModule,
    TranslateModule,
    CommonModule,
    FlexModule,
    NzTooltipModule,
    NzIconModule,
    MatIconModule,
    TooltipComponent,
    NzSwitchModule,
    FormsModule,
    NzButtonModule,
    NzModalModule,
    NzPaginationModule,
    BrowserModule,
    PdfViewerModule,
    NzSpinModule,
    NzImageModule,
    PermissionModule,
  ],
  templateUrl: './care-plan-file.component.html',
  styleUrl: './care-plan-file.component.css',
  standalone: true,
})
export class CarePlanFileComponent {
  @Input() isPreview: boolean = false;
  @Input() isLoading?: boolean = false;
  isPreviewLoading?: boolean = false;

  @Input() pagination?: boolean = true;

  public addComp = CarePlanFileAddComponent;

  private destroy$ = new Subject<void>();

  pdfObj?: string;
  pdfItem?: ICarePlanFileDetailed;

  @Input() data?: ICarePlanFileDetailed[];

  currentPageIndex: number = 1;
  pageSize: number = 5;

  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef; // Reference to the modal
  isVisible = false;

  fileType?: string;
  fileName?: string;

  constructor(
    private readonly carePlanFileService: CarePlanFileService,
    private readonly eocService: EpisodeOfCareService,
    private readonly router: Router,
    private readonly swalToastService: SwalToastService,
    private readonly translate: TranslateService,
    private readonly permissionService: PermissionService
  ) {}

  ngOnInit() {
    this.carePlanFileService.carePlanFileLoadingSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: boolean) => {
        this.isLoading = data;
      });

    if (!this.data) {
      this.carePlanFileService.carePlanFileDataSub
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: ICarePlanFileDetailed[]) => {
          this.data = data;

          if (data.at(0)) {
            this.requestPDF(data.at(0));
          }
        });

      this.carePlanFileService.getData();
    }
  }

  isEOC() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('episodeOfCare')
    );
  }

  isInpatientView() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('inPatientView')
    );
  }

  hasUnsubmitted() {
    return this.getData()?.find(
      (d) => d?.['isSubmitted'] !== undefined && !d?.['isSubmitted']
    );
  }

  getData() {
    if (this.pagination) {
      const startIndex = (this.currentPageIndex - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this?.data?.slice(startIndex, endIndex) || [];
    }
    return this?.data;
  }

  handlePageIndexChange(pageIndex: number): void {
    this.currentPageIndex = pageIndex;
  }

  handlePageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPageIndex = 1; // Reset to the first page when changing page size
  }

  openModal(item?: ICarePlanFileDetailed) {
    this.carePlanFileService.carePlanFileModalSub.next(true);
    this.carePlanFileService.carePlanFileEditSub.next(item);
  }

  deleteFun(id: number) {
    Swal.fire({
      text: `${this.translate.instant(
        'deletePlaceholder1'
      )} ${this.translate.instant('Care Plan')}?`,
      showDenyButton: false,
      showCancelButton: true,
      cancelButtonText: `${this.translate.instant('Cancel')}`,
      confirmButtonText: `${this.translate.instant('Ok')}`,
    }).then((result) => {
      if (result.isConfirmed) {
        this.carePlanFileService.deleteCarePlanFile(id.toString()).subscribe({
          next: (d) => {
            this.swalToastService.toastSuccess(
              'Care Plan File deleted successfully'
            );
            if (id == this?.pdfItem?.id) {
              this.pdfObj = undefined;
              this.pdfItem = undefined;
            }

            this.carePlanFileService.deleteCarePlanFileFromTable(id.toString());
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to delete Care Plan File');
          },
        });
      } else if (result.isDenied) {
        Swal.fire(
          `${this.translate.instant('Care Plan File is not deleted')}`,
          '',
          'info'
        );
      }
    });
  }

  requestPDF(item?: ICarePlanFileDetailed) {
    this.isPreviewLoading = true;
    this.carePlanFileService.getPDF(item?.id?.toString()!).subscribe({
      next: (res: Blob) => {
        if (!ALLOWED_MIME.includes(res.type as any)) {
          this.swalToastService.toastError('Unsupported file type.');
          this.isPreviewLoading = false;
          this.pdfObj = undefined;
          this.pdfItem = undefined;
          this.fileType = undefined;
          this.fileName = undefined;
          return;
        }
        if (this.pdfObj) URL.revokeObjectURL(this.pdfObj);
        this.pdfObj = URL.createObjectURL(res);
        this.fileType = res.type || undefined;
        this.pdfItem = item;
        const filePath = item?.path || '';
        const fileNameIndex = filePath.lastIndexOf('/');
        this.fileName =
          fileNameIndex === -1
            ? item?.examTitle
            : filePath.substring(fileNameIndex + 1);
        this.isPreviewLoading = false;
      },
      error: () => {
        this.swalToastService.toastError('Unable to retrieve the document');
        if (this.pdfObj) URL.revokeObjectURL(this.pdfObj);
        this.pdfObj = undefined;
        this.pdfItem = undefined;
        this.fileType = undefined;
        this.fileName = undefined;
        this.isPreviewLoading = false;
      },
    });
  }

  downloadPDF(item: ICarePlanFileDetailed) {
    this.carePlanFileService.downloadPDF(item);
  }

  getValue(field: string, value?: string) {
    if (!value) {
      return '-';
    }

    if (field?.toLowerCase()?.includes('date time')) {
      return moment(value).format(datetime_format);
    } else if (field?.toLowerCase()?.includes('date')) {
      return moment(value).format(date_format);
    } else {
      return value;
    }
  }

  handleCancel() {
    this.isVisible = false;

    this.modalRef.destroy();
  }

  hasPermission(permission: string[]) {
    return this.permissionService.can(permission);
  }
}
